import type { ContractInfo, User } from 'api/data.types'
import { Acervo, Degree, SpecialEntities } from 'utils/user'
import type { UserState } from 'utils/hooks/use-userInfo'
import { FREEMIUM_ID } from 'utils/header/constants'
import { PERMISSIONS } from 'config/vars'
import { Permission as PermissionsEnum } from 'utils/permissions'

export enum FeaturesEnum {
  contentReader = 'content::reader',
  contentPressReader = 'content::pressreader',
  bookProgressBar = 'content::remove_progress_bar',
  recommendations = 'reader_experience::recommendations',
  myReadings = 'reader_experience::my_readings',
  favorites = 'reader_experience::favorites',
  activities = 'reader_experience::activities',
  relatedContent = 'reader_experience::related_content',
  discoverySearch = 'discovery::search',
  discoveryCategories = 'discovery::categories',
  forest = 'gamification::forest',
  league = 'gamification::league',
  usageReports = 'teacher_experience::usage_reports',
  customCollections = 'teacher_experience::custom_collections',
  entityUsersManagement = 'teacher_experience::entity_users_management',
  didacticSequences = 'teacher_experience::didactic_sequences',
  adminPanel = 'teacher_experience::admin_panel',
  manageActivity = 'teacher_experience::manage_activity',
  readerV2 = 'feature::novo_leitor',
  runningRecords = 'assessment::running_records',
  contextNonEducational = 'context::non_educational',
  readingFluency = 'assessment::reading_fluency',
  applyNewFluencyActivity = 'assessment::apply_new_activity',
  disableWeeklyReadingReportNotification = 'notification::disable_weekly_reading_report',
  canManageAppliedActivity = 'activities::can_manage_applied_activity'
}

/**
 * Used to get user feature permissions
 * @param user User object
 * @returns Array of objects that represents the enabled/disabled features
 */
export function getFeaturesPermissions(user: User): UserState {
  const featuresArray = user?.reader?.entity?.contractInfo?.features || []
  const featObj = convertFeaturesToObject(featuresArray)
  const shouldSeePressreader = !Degree.isKids(user)
  const isTeacher = user?.teacher && user?.teacher?.id !== null
  const isArvoreAdmin = user?.isAdmin
  const isPositivo = SpecialEntities.isPositivo(user)

  const newPermissionsManagement =
    user?.featureToggles?.newPermissionsManagement

  const getHasAdminPermission = () => {
    if (newPermissionsManagement) {
      return Boolean(
        user?.permissions?.includes(PermissionsEnum.CAN_ACCESS_TEACHER_PANEL)
      )
    }

    return Boolean(
      user?.teacher?.permissions
        ?.split(' ')
        ?.includes(PERMISSIONS.CAN_ACCESS_ADMIN)
    )
  }

  const getCanAccessAdminPanel = () => {
    if (!featObj[FeaturesEnum.adminPanel]?.enabled) {
      return false
    }

    if (isArvoreAdmin) {
      return true
    }

    const hasAdminPermission = getHasAdminPermission()

    if (isPositivo) {
      return hasAdminPermission
    }

    return newPermissionsManagement ? hasAdminPermission : isTeacher
  }

  return {
    canAccessReader: featObj[FeaturesEnum.contentReader]?.enabled,
    canAccessPressreaderContent: featObj[FeaturesEnum.contentPressReader]
      ?.enabled
      ? shouldSeePressreader
      : false,
    canAccessBookProgressBar: !featObj[FeaturesEnum.bookProgressBar]?.enabled,
    canAccessIndications: featObj[FeaturesEnum.recommendations]?.enabled,
    canAccessMyReadings: featObj[FeaturesEnum.myReadings]?.enabled,
    canAccessFavorites: featObj[FeaturesEnum.favorites]?.enabled,
    canAccessActivities: featObj[FeaturesEnum.activities]?.enabled,
    canAccessRelatedContent: featObj[FeaturesEnum.relatedContent]?.enabled,
    canAccessSearch: featObj[FeaturesEnum.discoverySearch]?.enabled,
    canAccessCategories: featObj[FeaturesEnum.discoveryCategories]?.enabled,
    canAccessForest: featObj[FeaturesEnum.forest]?.enabled,
    canAccessLeagueContent: featObj[FeaturesEnum.league]?.enabled,
    canAccessUsageReports: featObj[FeaturesEnum.usageReports]?.enabled,
    canAccessCustomCollections:
      featObj[FeaturesEnum.customCollections]?.enabled,
    canAccessDidacticSequences:
      featObj[FeaturesEnum.didacticSequences]?.enabled,
    canAccessEntityUsersManagement:
      featObj[FeaturesEnum.entityUsersManagement]?.enabled,
    canAccessAdminPanel: getCanAccessAdminPanel(),
    canAccessReaderV2: featObj[FeaturesEnum.readerV2]?.enabled,
    canAccessRunningRecords: featObj[FeaturesEnum.runningRecords]?.enabled,
    canAccessReadingFluency: featObj[FeaturesEnum.readingFluency]?.enabled,
    canAccessApplyNewFluencyActivity:
      featObj[FeaturesEnum.applyNewFluencyActivity]?.enabled,
    canManageActivity: featObj[FeaturesEnum.manageActivity]?.enabled ?? true,
    isContextNonEducational:
      featObj[FeaturesEnum.contextNonEducational]?.enabled,
    isDisableWeeklyReadingReportNotification:
      featObj[FeaturesEnum.disableWeeklyReadingReportNotification]?.enabled,
    canManageAppliedActivity:
      featObj[FeaturesEnum.canManageAppliedActivity]?.enabled ?? true
  }
}

/**
 * Used to get user old feature permissions
 * @param user User object
 * @returns Array of objects that represents the enabled/disabled features
 * @deprecated use 'getFeaturesPermissions' if possible. This function will be removed in a future release.
 */
export function getOldFeaturesPermissions(user: User): UserState {
  const reader = user?.reader
  const teacher = user?.teacher
  const isTeacher = teacher && teacher?.id !== null

  const canAccessShareReading =
    isTeacher && user?.featureToggles?.sharedReadingEnabled
  const canAccessCollections = (user?.reader?.acervos?.length ?? 0) > 0
  const canAccessOnboarding = reader?.canAccessOnboarding

  return {
    canAccessCollections,
    canAccessShareReading,
    canAccessOnboarding
  }
}

/**
 * This convert the list of features into an object to improve dev experience
 * @param features Array of features
 * @returns Features object
 */
function convertFeaturesToObject(features: ContractInfo['features']) {
  const featuresObject = {}

  features.map(
    item => (featuresObject[item?.name] = { enabled: item?.enabled })
  )

  return featuresObject
}

export type VisibleChecklists = {
  showInterests: boolean
  showReading: boolean
  showConquests: boolean
  showTree: boolean
}

/**
 * Determines which checklist items should be visible based on contractInfo features
 * @param user User object
 * @returns Object with flags indicating which checklists should be shown
 */
export function getVisibleChecklists(user: User): VisibleChecklists {
  const featuresArray = user?.reader?.entity?.contractInfo?.features || []
  const featObj = convertFeaturesToObject(featuresArray)

  const forestEnabled = featObj[FeaturesEnum.forest]?.enabled ?? false
  const leagueEnabled = featObj[FeaturesEnum.league]?.enabled ?? false

  const showConquests = forestEnabled || leagueEnabled
  const showTree = forestEnabled

  return {
    showInterests: true,
    showReading: true,
    showConquests,
    showTree
  }
}

export function getUserProps(user: User): UserState {
  const reader = user?.reader
  const teacher = user?.teacher
  const rootAcervoEnglish = Acervo.isEnglish(user)
  const rootAcervoEnglishOnly = Acervo.isEnglishOnly(user)
  const isEnglishTrial = Boolean(
    !rootAcervoEnglish &&
      !rootAcervoEnglishOnly &&
      teacher &&
      teacher?.englishTrial &&
      teacher?.englishTrial?.remainingDays > 0
  )
  const isClientSomos = reader?.root?.id === 64781
  const isArvore = SpecialEntities.isArvore(user)
  const isPositivo = SpecialEntities.isPositivo(user)
  const isPositivoPremiumEntity = SpecialEntities.isPositivoPremiumEntity(user)
  const hasFreemiumBooks = isPositivo && !isPositivoPremiumEntity
  const isFreemium = reader?.root?.id === FREEMIUM_ID
  const isTeacher = Boolean(teacher && teacher?.id !== null)

  return {
    ...user,
    isTeacher,
    isEnglishTrial,
    hasFreemiumBooks,
    isPositivo,
    isClientSomos,
    isArvore,
    isFreemium
  }
}
